import type { UIEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdImage } from '../types';
import { generateSrcSet } from '../utils';

type ImagesOverviewProps = {
    images: AdImage[];
    onClick: (e: UIEvent, page: number) => void;
    currentImage: number;
    isMobile?: boolean;
    hidden?: boolean;
    showTabs?: boolean;
};

export const ImagesOverview = (props: ImagesOverviewProps) => {
    const { images, onClick, currentImage, isMobile, hidden } = props;
    const imageRefs = useRef<(HTMLImageElement | null)[]>([]);
    const [behavior, setBehavior] = useState<ScrollBehavior>('instant');

    useEffect(() => {
        imageRefs.current[currentImage]?.focus();
    }, [currentImage]);

    const imageSize = props.images.length;

    const isElementInView = (element: Element, container: Element): boolean => {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        return elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;
    };

    useEffect(() => {
        if (!isMobile && !hidden) {
            setBehavior('smooth');
        }
    }, [hidden]);

    const IMAGE_OVERVIEW_MARGIN = 100;
    const updateOverview = useCallback(() => {
        const imageOverview = imageRefs.current[currentImage];
        const container = imageOverview?.parentElement?.parentElement?.parentElement;

        if (!imageOverview || !container || hidden) return;

        if (!isElementInView(imageOverview, container)) {
            const thumbnailRect = imageOverview.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            if (thumbnailRect.top < containerRect.top) {
                imageOverview.scrollIntoView({ block: 'center', behavior: behavior });
                if (container.scrollLeft > 0) {
                    container.scrollBy({ top: -IMAGE_OVERVIEW_MARGIN, behavior: behavior });
                }
            } else if (thumbnailRect.bottom > containerRect.bottom) {
                imageOverview.scrollIntoView({ block: 'center', behavior: behavior });
                if (container.scrollLeft + container.clientWidth < container.scrollWidth) {
                    container.scrollBy({ top: IMAGE_OVERVIEW_MARGIN, behavior: behavior });
                }
            }
        }
    }, [hidden, currentImage]);

    const containerRef = useRef<HTMLUListElement>(null);

    const hideMobileView = hidden && isMobile;

    return (
        <ul
            className={`flex flex-col overflow-y-auto col-span-1 gap-16 hide-scroll outline-transparent ${
                isMobile ? 'gallery-remaining-mobile' : ''
            } ${hideMobileView ? 'hidden' : ''}`}
            tabIndex={-1}
            ref={containerRef}
            style={{ height: isMobile ? '' : 'calc(100vh - 73px)' }}>
            {images.map((image, idx) => (
                <li key={`overview-image-${image.uri}`} className="justify-center p-2 thumbnailSize" onClick={(e) => onClick(e, idx)}>
                    <img
                        className="object-cover w-full rounded-4 hover:cursor-pointer"
                        ref={(r: HTMLImageElement) => {
                            if (r) {
                                imageRefs.current[idx] = r;
                                updateOverview();
                            }
                        }}
                        style={{
                            aspectRatio: isMobile ? String(image.width / image.height) : '3/2',
                            outline: idx === currentImage && !isMobile ? '2px solid var(--w-s-color-border-inverted)' : '',
                        }}
                        src={image.uri}
                        alt="Galleribilde"
                        srcSet={generateSrcSet(image.uri)}
                        title={`${image.description || ''}, bilde ${idx + 1} av ${imageSize}`}
                        loading="lazy"
                    />
                </li>
            ))}
        </ul>
    );
};
